// „Lerne aus Fehlern“ nach der Partie: jede Stellung, in der ein Fehler passiert ist, noch einmal.
// Der beste Zug löst sie; ein anderer Zug, den der Coach nicht als Fehler wertet, zählt auch.
import { chessgroundDests } from 'chessops/compat';
import { parseSquare } from 'chessops/util';
import type { DrawShape } from '@lichess-org/chessground/draw';
import type { Key } from '@lichess-org/chessground/types';
import { coachApi } from './coachapi';
import type { Slip } from './playcoach';
import { PlayGame } from './playgame';

export type TrainState = 'ask' | 'checking' | 'wrong' | 'right' | 'shown';

export class Training {
  index = 0;
  solved = 0;
  state: TrainState = 'ask';
  board: PlayGame;

  constructor(readonly slips: Slip[], private readonly redraw: () => void) {
    this.board = new PlayGame(slips[0].before);
  }

  get slip() {
    return this.slips[this.index];
  }

  get done() {
    return this.index >= this.slips.length;
  }

  get fen() {
    return this.board.fen;
  }

  get color() {
    return this.slip.before.split(' ')[1] === 'b' ? 'black' : 'white';
  }

  get last(): [Key, Key] | undefined {
    return this.board.moves[0]?.last;
  }

  dests() {
    return this.state === 'ask' || this.state === 'wrong' ? chessgroundDests(this.board.pos) : new Map();
  }

  shapes(): DrawShape[] {
    const b = this.slip?.best;
    return this.state === 'shown' && b ? [{ orig: b.slice(0, 2) as Key, dest: b.slice(2, 4) as Key, brush: 'green' }] : [];
  }

  /** Zug in der Aufgabe; Umwandlung immer in eine Dame */
  async move(orig: Key, dest: Key) {
    const slip = this.slip;
    const promo = this.board.pos.board.get(parseSquare(orig)!)?.role === 'pawn' && /[18]/.test(dest[1]);
    if (!this.board.play({ from: parseSquare(orig)!, to: parseSquare(dest)!, promotion: promo ? 'queen' : undefined }, [orig, dest])) return;
    let good = orig + dest === slip.best.slice(0, 4);
    if (!good) {
      this.state = 'checking';
      this.redraw();
      try {
        const r = await coachApi.check(slip.before, this.fen);
        good = r.judgement === null || r.judgement === 'inaccuracy';
      } catch {
        good = false;
      }
      if (slip !== this.slip) return;
    }
    if (good) this.solved++;
    else this.board.undo(1);
    this.state = good ? 'right' : 'wrong';
    this.redraw();
  }

  reveal() {
    this.state = 'shown';
    this.redraw();
  }

  next() {
    this.index++;
    this.state = 'ask';
    if (!this.done) this.board = new PlayGame(this.slip.before);
    this.redraw();
  }
}
